// ── Service de Leads (núcleo/aplicação) ───────────────────────────
// Regras/validações do domínio. Recebe o repositório via porta (DI) — não
// conhece Supabase. Instanciado no composition root (src/app/core.ts).

import type { ILeadRepository, CriarLeadParams, CreateLeadDirectParams, LeadGraphVisibility } from '../ports/ILeadRepository';
import type { LeadWithGraph, LeadDetail, LeadSearchResult, LeadUpdateInput, MobileLeadsPage } from '../leadModel';

export class LeadService {
  constructor(private readonly repo: ILeadRepository) {}

  createExternalLead = async (params: CriarLeadParams): Promise<unknown> => {
    if (!params?.workspace_slug) throw new Error('workspace_slug e obrigatorio.');
    if (!params?.nome) throw new Error('nome e obrigatorio.');
    return this.repo.createExternalLead(params);
  };

  importLead = async (params: CriarLeadParams): Promise<unknown> => {
    if (!params?.workspace_slug) throw new Error('workspace_slug e obrigatorio.');
    if (!params?.nome) throw new Error('nome e obrigatorio.');
    return this.repo.importLead(params);
  };

  createFunnel = async (
    workspaceSlug: string,
    nome: string,
    descricao: string | null = null,
  ): Promise<unknown> => {
    if (!workspaceSlug) throw new Error('workspace_slug e obrigatorio.');
    if (!nome) throw new Error('nome e obrigatorio.');
    return this.repo.createFunnel(workspaceSlug, nome, descricao);
  };

  getLeadDetail = (leadId: string): Promise<LeadDetail | null> => {
    if (!leadId) throw new Error('leadId e obrigatorio.');
    return this.repo.getLeadDetail(leadId);
  };

  updateLead = (leadId: string, updates: LeadUpdateInput): Promise<LeadDetail | null> => {
    if (!leadId) throw new Error('leadId e obrigatorio.');
    return this.repo.updateLead(leadId, updates);
  };

  patchLead = (leadId: string, updates: LeadUpdateInput): Promise<void> => {
    if (!leadId) throw new Error('leadId e obrigatorio.');
    return this.repo.patchLead(leadId, updates);
  };

  deactivateLead = (leadId: string): Promise<void> => {
    if (!leadId) throw new Error('leadId e obrigatorio.');
    return this.repo.deactivateLead(leadId);
  };

  searchLeads = (workspaceId: string, query: string): Promise<LeadSearchResult[]> => {
    if (!workspaceId || !query?.trim()) return Promise.resolve([]);
    return this.repo.searchLeads(workspaceId, query.trim());
  };

  listMobileMyLeads = (workspaceId: string, pipelineId: string, search = '', limit = 10, offset = 0): Promise<MobileLeadsPage> => {
    if (!workspaceId || !pipelineId) return Promise.resolve({ items: [], total: 0 });
    return this.repo.listMobileMyLeads(workspaceId, pipelineId, search.trim(), limit, offset);
  };

  listLeadsGraph = (
    workspaceId: string,
    visibility?: LeadGraphVisibility | null,
    pipelineIds?: string[] | null,
  ): Promise<LeadWithGraph[]> => {
    if (!workspaceId) return Promise.resolve([]);
    // Recorte Enterprise pediu leads mas o time ainda não resolveu → fail-closed
    // (evita vazar todo o workspace enquanto os IDs não carregam).
    if (visibility && visibility.ownerIds.length === 0) return Promise.resolve([]);
    return this.repo.listLeadsGraph(workspaceId, visibility ?? null, pipelineIds ?? null);
  };

  createLeadWithInitialCard = (params: CreateLeadDirectParams): Promise<{ lead_id: string }> => {
    if (!params?.workspaceId) throw new Error('workspaceId e obrigatorio.');
    if (!params?.nome) throw new Error('nome e obrigatorio.');
    return this.repo.createLeadWithInitialCard(params);
  };

  passLeadToCloser = (workspaceId: string, leadId: string, sdrId: string | null): Promise<void> => {
    if (!workspaceId) throw new Error('workspaceId e obrigatorio.');
    if (!leadId) throw new Error('leadId e obrigatorio.');
    return this.repo.passLeadToCloser(workspaceId, leadId, sdrId);
  };

  sendLeadToCloserPipeline: ILeadRepository['sendLeadToCloserPipeline'] = (params) => {
    if (!params?.leadId) throw new Error('leadId e obrigatorio.');
    if (!params?.sdrCardId) throw new Error('sdrCardId e obrigatorio.');
    if (!params?.closerPipelineId) throw new Error('closerPipelineId e obrigatorio.');
    return this.repo.sendLeadToCloserPipeline(params);
  };

  handoffLeadToComercial: ILeadRepository['handoffLeadToComercial'] = (params) => {
    if (!params?.leadId) throw new Error('leadId e obrigatorio.');
    if (!params?.sdrCardId) throw new Error('sdrCardId e obrigatorio.');
    if (!params?.closerId) throw new Error('closerId e obrigatorio.');
    if (!params?.closerPipelineId) throw new Error('closerPipelineId e obrigatorio.');
    return this.repo.handoffLeadToComercial(params);
  };
}
